import type { SyncProvider, SharedFolderInfo } from './syncProvider.js';

/**
 * F1-a — turn-based multiplayer rooms over the F2 shared-Drive plumbing.
 * Web mirror of the Kotlin GameRoom (see GameRoom.kt for the full design).
 *
 * A room is a visible Drive folder ("anyone with the link can edit" — the
 * link is the invite). Every player owns exactly one file, `p_<id>.json`,
 * an append-only log with a monotonic seq — nobody writes anyone else's
 * file, so games get ordering without merge. Sends coalesce (~1.2 s) and
 * the folder is polled every ~2 s: one-way delivery lands around 2–5 s.
 *
 * This file is loaded inside the MODULE's iframe (moduleHost context), so
 * the poll interval dies with the module — no teardown plumbing needed.
 */

const POLL_MS = 2_000;
const FLUSH_MIN_GAP_MS = 1_200;
const KEEP_MSGS = 50;
const INBOX_CAP = 200;
const PLAYER_ID_KEY = 'shell.room.playerId';

interface Msg { s: number; t: number; d: string }
interface MemberFile {
  v: number; id: string; name: string; joinedT: number; seq: number; msgs: Msg[];
}
export interface RoomMember { id: string; name: string; joinedT: number; lastSeenMs: number }

interface Session {
  folderId: string;
  folderName: string;
  link: string;
  playerId: string;
  playerName: string;
  provider: SyncProvider;
  joinedT: number;
  seq: number;
  pending: Msg[];
  sent: Msg[];
  lastFlushMs: number;
  flushTimer: number | null;
  flushing: boolean;
  pollTimer: number | null;
  polling: boolean;
  lastDelivered: Map<string, number>;
  fileStamp: Map<string, number>;
  members: Map<string, RoomMember>;
  inbox: string[];
  deliver: ((payload: string) => void) | null;
}

let session: Session | null = null;

/** Stable per-browser player id (independent of the sync device id). */
export function roomPlayerId(): string {
  let id = localStorage.getItem(PLAYER_ID_KEY);
  if (!id) {
    id = 'p' + crypto.randomUUID().replace(/-/g, '').slice(0, 10);
    localStorage.setItem(PLAYER_ID_KEY, id);
  }
  return id;
}

/**
 * Binds to an already-validated room folder, announces this player by
 * writing their (empty) log file, and starts polling. Used by both create
 * and join.
 */
export async function openRoom(
  provider: SyncProvider,
  info: SharedFolderInfo,
  playerName: string,
): Promise<{ playerId: string }> {
  leaveRoom();
  const s: Session = {
    folderId: info.id,
    folderName: info.name,
    link: info.link,
    playerId: roomPlayerId(),
    playerName: playerName.trim() || 'Player',
    provider,
    joinedT: Date.now(),
    seq: 0,
    pending: [],
    sent: [],
    lastFlushMs: 0,
    flushTimer: null,
    flushing: false,
    pollTimer: null,
    polling: false,
    lastDelivered: new Map(),
    fileStamp: new Map(),
    members: new Map(),
    inbox: [],
    deliver: null,
  };
  session = s;
  await flush(s); // announce membership before saying anything
  s.pollTimer = window.setInterval(() => { void pollOnce(s); }, POLL_MS);
  void pollOnce(s); // eager first poll so members appear fast
  return { playerId: s.playerId };
}

export function sendToRoom(data: string): void {
  const s = session;
  if (!s) return;
  s.seq += 1;
  s.pending.push({ s: s.seq, t: Date.now(), d: data });
  scheduleFlush(s);
}

export function setRoomListener(deliver: (payload: string) => void): void {
  const s = session;
  if (!s) return;
  s.deliver = deliver;
  const backlog = s.inbox.splice(0, s.inbox.length);
  for (const p of backlog) deliver(p);
}

export function leaveRoom(): void {
  const s = session;
  if (!s) return;
  session = null;
  if (s.flushTimer !== null) clearTimeout(s.flushTimer);
  if (s.pollTimer !== null) clearInterval(s.pollTimer);
  s.deliver = null;
}

export function roomStateJson(): string {
  const s = session;
  if (!s) return JSON.stringify({ active: false });
  const all = new Map(s.members);
  all.set(s.playerId, { id: s.playerId, name: s.playerName, joinedT: s.joinedT, lastSeenMs: Date.now() });
  const members = [...all.values()].sort((a, b) => a.joinedT - b.joinedT);
  return JSON.stringify({
    active: true,
    code: s.folderId,
    name: s.folderName,
    link: s.link,
    playerId: s.playerId,
    playerName: s.playerName,
    members,
  });
}

// ── Send side: coalesced flush of my own file ────────────────────────────

function scheduleFlush(s: Session): void {
  if (s.flushTimer !== null || s.flushing) return;
  const wait = Math.max(0, s.lastFlushMs + FLUSH_MIN_GAP_MS - Date.now());
  s.flushTimer = window.setTimeout(() => {
    s.flushTimer = null;
    void (async () => {
      s.flushing = true;
      try {
        await flush(s);
      } catch (e) {
        console.warn('[gameRoom] flush failed', e);
      } finally {
        s.flushing = false;
      }
      // Sends that queued during the upload get their own pass.
      if (s.pending.length && session === s) scheduleFlush(s);
    })();
  }, wait);
}

async function flush(s: Session): Promise<void> {
  s.sent.push(...s.pending);
  s.pending = [];
  if (s.sent.length > KEEP_MSGS) s.sent = s.sent.slice(-KEEP_MSGS);
  s.lastFlushMs = Date.now();
  const body: MemberFile = {
    v: 1, id: s.playerId, name: s.playerName, joinedT: s.joinedT, seq: s.seq, msgs: s.sent,
  };
  await s.provider.writeSharedFile?.(s.folderId, `p_${s.playerId}.json`, JSON.stringify(body));
}

// ── Receive side: poll the folder, read only files that changed ──────────

async function pollOnce(s: Session): Promise<void> {
  if (s.polling || session !== s) return;
  s.polling = true;
  try {
    const files = await s.provider.listSharedFolder?.(s.folderId);
    if (!files || session !== s) return;
    const myFile = `p_${s.playerId}.json`;
    for (const f of files) {
      if (!f.path.startsWith('p_') || !f.path.endsWith('.json')) continue;
      if (f.path === myFile) continue;
      if (s.fileStamp.get(f.path) === f.remoteModifiedMs) continue; // unchanged → skip the read
      const txt = await s.provider.readSharedFile?.(s.folderId, f.path);
      if (!txt || session !== s) continue;
      let mf: MemberFile;
      try { mf = JSON.parse(txt) as MemberFile; } catch { continue; }
      if (!mf.id) continue;
      s.fileStamp.set(f.path, f.remoteModifiedMs);
      s.members.set(mf.id, { id: mf.id, name: mf.name, joinedT: mf.joinedT, lastSeenMs: f.remoteModifiedMs });
      const known = s.lastDelivered.has(mf.id);
      const last = s.lastDelivered.get(mf.id) ?? -1;
      // First sight of a player replays only their NEWEST message — enough
      // for full-state protocols to catch up without replaying history.
      const msgs = mf.msgs ?? [];
      const lastMsg = msgs.length ? msgs[msgs.length - 1] : undefined;
      const fresh = !known
        ? (lastMsg ? [lastMsg] : [])
        : msgs.filter((m) => m.s > last).sort((a, b) => a.s - b.s);
      s.lastDelivered.set(mf.id, Math.max(mf.seq, last));
      for (const m of fresh) {
        const payload = JSON.stringify({ from: mf.id, name: mf.name, data: m.d, seq: m.s, t: m.t });
        if (s.deliver) s.deliver(payload);
        else {
          s.inbox.push(payload);
          if (s.inbox.length > INBOX_CAP) s.inbox.splice(0, s.inbox.length - INBOX_CAP);
        }
      }
    }
  } catch (e) {
    console.warn('[gameRoom] poll failed', e);
  } finally {
    s.polling = false;
  }
}
